import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { markdownToHtml } from "@/lib/markdown";
import { printMaterialToPDF } from "@/lib/pdf";
import { toast } from "sonner";
import { DISCIPLINAS, SERIES } from "@/lib/options";
import { ArrowLeft, Copy, Download, Save, Trash2, Files, Filter } from "lucide-react";

const TIPO_LABEL = { plano: "Plano de Aula", atividade: "Atividade", avaliacao: "Avaliação", adaptacao: "Adaptação" };
const TIPOS = ["plano", "atividade", "avaliacao", "adaptacao"];

export function MateriaisPage() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ tipo: "", disciplina: "", serie: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.disciplina) params.disciplina = filters.disciplina;
      if (filters.serie) params.serie = filters.serie;
      const { data } = await api.get("/materiais", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const remove = async (id) => {
    if (!window.confirm("Excluir este material?")) return;
    try { await api.delete(`/materiais/${id}`); load(); toast.success("Excluído."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicate = async (id) => {
    try { await api.post(`/materiais/${id}/duplicate`); load(); toast.success("Duplicado."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Meus Materiais</h1>
      <p className="mt-2 text-slate-600">Tudo o que você criou fica salvo aqui.</p>

      <div className="mt-6 flex flex-wrap items-end gap-3 p-4 bg-white rounded-2xl border border-slate-200">
        <Filter className="text-slate-500 mb-2" size={18}/>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Tipo</Label>
          <Select value={filters.tipo} onValueChange={v => setFilters({...filters, tipo: v === "all" ? "" : v})}>
            <SelectTrigger className="w-44 rounded-xl mt-1" data-testid="filter-tipo"><SelectValue placeholder="Todos"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {TIPOS.map(t => <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Disciplina</Label>
          <Select value={filters.disciplina} onValueChange={v => setFilters({...filters, disciplina: v === "all" ? "" : v})}>
            <SelectTrigger className="w-56 rounded-xl mt-1" data-testid="filter-disciplina"><SelectValue placeholder="Todas"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{DISCIPLINAS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Série</Label>
          <Select value={filters.serie} onValueChange={v => setFilters({...filters, serie: v === "all" ? "" : v})}>
            <SelectTrigger className="w-56 rounded-xl mt-1" data-testid="filter-serie"><SelectValue placeholder="Todas"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{SERIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? <div className="text-slate-500">Carregando...</div> :
          items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
              <p className="text-slate-600">Nenhum material encontrado.</p>
            </div>
          ) : items.map(m => (
            <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl shadow-sm gap-3" data-testid={`material-row-${m.id}`}>
              <Link to={`/materiais/${m.id}`} className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest font-bold text-[#8B5CF6]">{TIPO_LABEL[m.tipo]}</div>
                <div className="font-heading font-semibold text-slate-900 truncate">{m.titulo}</div>
                <div className="text-xs text-slate-500 mt-0.5">{m.disciplina} · {m.serie} · {new Date(m.created_at).toLocaleDateString("pt-BR")}</div>
              </Link>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => duplicate(m.id)} data-testid={`btn-dup-${m.id}`}>
                  <Files size={14}/>
                </Button>
                <Button variant="outline" size="sm" className="rounded-full text-red-600 hover:text-red-700 hover:border-red-300" onClick={() => remove(m.id)} data-testid={`btn-del-${m.id}`}>
                  <Trash2 size={14}/>
                </Button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

export function MaterialDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [conteudo, setConteudo] = useState("");
  const [titulo, setTitulo] = useState("");
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/materiais/${id}`).then(r => {
      setItem(r.data); setConteudo(r.data.conteudo); setTitulo(r.data.titulo);
    }).catch(e => { toast.error(formatApiError(e)); nav("/materiais"); });
  }, [id, nav]);

  if (!item) return <div className="p-10">Carregando...</div>;

  const save = async () => {
    try {
      const { data } = await api.put(`/materiais/${id}`, { titulo, conteudo });
      setItem(data); toast.success("Alterações salvas.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copy = async () => { await navigator.clipboard.writeText(conteudo); toast.success("Copiado!"); };
  const download = () => printMaterialToPDF({ ...item, titulo, conteudo }, user?.name);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <Link to="/materiais" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-materiais">
        <ArrowLeft size={14}/> Voltar para Meus Materiais
      </Link>
      <div className="text-xs uppercase tracking-widest font-bold text-[#8B5CF6]">{TIPO_LABEL[item.tipo]}</div>
      <Input value={titulo} onChange={e => setTitulo(e.target.value)} className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold h-14 border-none px-0 focus-visible:ring-0" data-testid="material-title"/>
      <div className="text-sm text-slate-500 mb-6">{item.disciplina} · {item.serie} · {item.tema}</div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">Editar</Label>
          <Textarea value={conteudo} onChange={e => setConteudo(e.target.value)} className="min-h-[520px] font-mono text-sm rounded-xl" data-testid="content-editor"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">Prévia</Label>
          <div className="min-h-[520px] max-h-[520px] overflow-y-auto rounded-xl border border-slate-200 p-5 bg-white prose-ap" dangerouslySetInnerHTML={{ __html: markdownToHtml(conteudo) }}/>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={copy} variant="outline" className="rounded-full" data-testid="btn-copy"><Copy className="mr-2" size={16}/> Copiar</Button>
        <Button onClick={save} className="rounded-full bg-[#8B5CF6] hover:bg-[#7c4ef4]" data-testid="btn-save"><Save className="mr-2" size={16}/> Salvar alterações</Button>
        <Button onClick={download} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-download-pdf"><Download className="mr-2" size={16}/> Baixar PDF</Button>
      </div>
    </div>
  );
}
